import { BuyerApi } from 'api/BuyerApi';
import { CompanyApi } from 'api/CompanyApi';
import { ProjectImportProductsModal } from 'components/buyer/project/ProjectImportProductsModal/ProjectImportProductsModal';
import { ProjectOrderImportExcel } from 'components/buyer/projectOrder/ProjectOrderImportExcel';
import { DeliveryAddressesNew } from 'components/buyer/settings/deliveryAddresses/DeliveryAddressNew/DeliveryAddressesNew';
import {
  getAddressById,
  getAllAddresses,
} from 'components/buyer/settings/deliveryAddresses/utilsDeliveryAddresses';
import { AppContext } from 'contexts/user/AppContext';
import dayjs from 'dayjs';
import { useSetCurrentProject } from 'hooks/useSetCurrentProject';
import { useSetUserProfile } from 'hooks/useSetUserProfile';
import { ProjectOrderModel } from 'models/buyer/projectOrder/ProjectOrderModels';
import { LoginResponseUserModel } from 'models/identity/LoginResponseUserModel';
import {
  AddressModel,
  AddressModelById,
} from 'models/shared/address/AddressModel';
import { getProjectOrderDetails } from 'pages/buyer/ProjectOrder/utilsProjectOrder';
import { FormEvent, useContext, useEffect, useRef, useState } from 'react';
import { useAuthUser } from 'react-auth-kit';
import {
  Button,
  Card,
  Col,
  Form,
  FormControl,
  InputGroup,
  Row,
} from 'react-bootstrap';
import { ArrowCounterclockwise, PencilSquare, X } from 'react-bootstrap-icons';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import 'react-day-picker/lib/style.css';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

type PropTypes = {
  projectDetails: ProjectOrderModel;
  projectId: number;
  formik: any;
  getDataSource: () => Promise<void>;
  setProjectDetails: (value: ProjectOrderModel) => void;
  setShowDisabledRows: (value: boolean) => void;
  showDisabledRows: boolean;
};

export const ProjectOrderHeader = ({
  projectDetails,
  projectId,
  formik,
  getDataSource,
  setProjectDetails,
  setShowDisabledRows,
  showDisabledRows,
}: PropTypes) => {
  const [isNameEditing, setIsNameEditing] = useState<boolean>(false);
  const [show, setShow] = useState(false);
  const [shownView, setShownView] = useState<'orders' | 'inquiries'>(
    'inquiries'
  );

  const [asOptionShow, setAsOptionShow] = useState<boolean>(false);

  const [isoDate, setIsoDate] = useState<string | null>(null);
  const [requestedDate, setRequestedDate] = useState<string | null>('');

  const [deliveryAddressId, setDeliveryAddressId] = useState<number>(0);

  const [addresses, setAddresses] = useState<AddressModel>({} as AddressModel);
  const [filteredAddresses, setFilteredAddresses] = useState<
    AddressModelById[]
  >([]);

  const authUser = useAuthUser();
  const currentUser = authUser() as LoginResponseUserModel;
  const { t } = useTranslation('components');

  const setUserProfile = useSetUserProfile();

  const setCurrentProject = useSetCurrentProject();

  const { appState } = useContext(AppContext);

  /* Get all addresses */
  useEffect(() => {
    const getAddresses = async () => {
      await getAllAddresses(
        currentUser,
        setAddresses,
        undefined,
        undefined,
        undefined,
        'isDefault_desc'
      );
    };

    getAddresses();
  }, [currentUser]);

  useEffect(() => {
    /* filter out not visible address */

    let clonedAddress = { ...addresses };

    if (clonedAddress.data !== undefined) {
      const filtered = clonedAddress.data.filter(
        (address) => address.isVisible
      );

      setFilteredAddresses(filtered);
    }
  }, [addresses]);

  useEffect(() => {
    addresses.data !== undefined &&
      addresses.data[0] !== undefined &&
      projectDetails.deliveryAddressId &&
      setDeliveryAddressId(
        projectDetails.deliveryAddressId !== addresses.data[0].id
          ? projectDetails.deliveryAddressId
          : addresses.data[0].id
      );
  }, [addresses.data, projectDetails.deliveryAddressId]);

  const inputFile = useRef<HTMLInputElement>(null);

  const handleProjectNameBlur = async (): Promise<void> => {
    setIsNameEditing(false);

    await BuyerApi.projects.updateProjectName(
      currentUser.companyId,
      projectId,
      {
        name: formik.values.name,
      }
    );

    const response = await BuyerApi.projectOrder.getProjectOrder(
      currentUser.companyId,
      projectDetails.id,
      projectId
    );

    await setCurrentProject(projectId);

    // If project name was changed, reload user profile with updated project name
    // into AppContext so that current project name in header is updated also
    await setUserProfile(appState.buyerCurrentSupplierId);

    setProjectDetails(response);
  };

  const handleKeyUp = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setIsNameEditing(false);
      await handleProjectNameBlur();
    }
  };

  const handleOpenFilePicker = () => {
    inputFile.current !== null && inputFile.current.click();
  };

  const handleAddNewFile = async (file: File) => {
    const reader = new FileReader();
    const formData = new FormData();

    formData.append('file', file);

    const input: HTMLInputElement | null =
      document.querySelector('input[type=file]');

    if (input !== null) {
      input.value = '';
    }

    reader.readAsDataURL(file);
    reader.onload = async function () {
      reader.result !== undefined &&
        (await CompanyApi.addNewOrderAttachment(
          currentUser.companyId,
          projectId,
          formData
        ));

      await getProjectOrderDetails(
        projectId,
        projectDetails.id,
        currentUser,
        setProjectDetails
      );
    };
  };

  const handleDeleteAttachment = async (id: number) => {
    let timeout: any = 0;

    toast.success(t('projectSettings.inf_DeletingFile'), {
      className: 'delete-attachment-toast',
      autoClose: 5000,
      pauseOnHover: true,
      closeButton: (
        <Button
          className='btn-undo-delete-attachment'
          onClick={() => clearTimeout(timeout)}
        >
          <ArrowCounterclockwise size={20} />
        </Button>
      ),
    });

    timeout = setTimeout(async () => {
      await CompanyApi.deleteOrderAttachment(
        currentUser.companyId,
        projectId,
        projectDetails.id,
        id
      );
      await getProjectOrderDetails(
        projectId,
        projectDetails.id,
        currentUser,
        setProjectDetails
      );
    }, 5000);
  };

  const handleOpenRecentInquiries = () => {
    setShow(true);
    setShownView('inquiries');
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleOpenRecentOrders = () => {
    setShow(true);
    setShownView('orders');
  };

  const disabledDays = {
    from: new Date(
      new Date().getFullYear() - 1,
      dayjs().startOf('month').month(),
      1
    ),
    to: new Date(
      dayjs().startOf('month').year(),
      dayjs().startOf('month').month(),
      new Date().getDate() - 1
    ),
  };

  const handleDateDelete = async () => {
    setRequestedDate('');
    setIsoDate(null);
  };

  const handleDateChange = async (day: Date) => {
    const pickedDate = dayjs(day).format('DD-MM-YYYY');

    setRequestedDate(pickedDate);
    setIsoDate(dayjs(day).toISOString());

    formik.setFieldValue('requestedDeliveryDate', dayjs(day).toISOString());

    formik.handleSubmit();
  };

  const setNewDeliverAddressAsSelected = async (value: number) => {
    const response = await getAddressById(currentUser, value);

    const clonedAddresses: AddressModel = { ...addresses };

    clonedAddresses.data.unshift(response);

    setAddresses(clonedAddresses);

    setDeliveryAddressId(value);
  };

  const handleDeliveryChange = async (event: FormEvent<HTMLSelectElement>) => {
    event.stopPropagation();

    const valueOfThisFunction =
      parseInt(event.currentTarget.value) === -1
        ? null
        : parseInt(event.currentTarget.value);

    if (formik.values.deliveryAddressId !== valueOfThisFunction) {
      if (event.currentTarget.value === 'addNewAddress') {
        setAsOptionShow(true);
      } else {
        setDeliveryAddressId(parseInt(event.currentTarget.value));
        formik.setFieldValue('deliveryAddressId', valueOfThisFunction);
        formik.handleSubmit();
      }
    }
  };

  useEffect(() => {
    if (projectDetails.requestedDeliveryDate) {
      setRequestedDate(
        dayjs(projectDetails.requestedDeliveryDate).format('DD-MM-YYYY')
      );

      setIsoDate(dayjs(projectDetails.requestedDeliveryDate).toISOString());
    } else {
      setRequestedDate(t('common.lbl_DDMMYYYY'));

      setIsoDate(null);
    }
  }, [projectDetails.requestedDeliveryDate]);

  return (
    <Card.Header className='px-2 py-0 '>
      <Row>
        <Col lg={6}>
          <Row className='mb-2'>
            <Col>
              {!isNameEditing ? (
                <h1>
                  {formik.values.name}{' '}
                  <PencilSquare
                    onClick={() => setIsNameEditing(true)}
                    style={{ cursor: 'pointer' }}
                    fontSize={'14px'}
                    className='icon-ez'
                  />
                </h1>
              ) : (
                <FormControl
                  name='name'
                  style={{ fontSize: '23px', fontWeight: '600' }}
                  className='borderless-input'
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  onBlur={handleProjectNameBlur}
                  onKeyUp={handleKeyUp}
                  aria-label='project-name'
                  autoFocus
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col>
              <div className='d-flex align-items-center'>
                <div className='me-3'>
                  <ProjectImportProductsModal
                    projectId={projectId}
                    getDataSource={getDataSource}
                    projectDetails={projectDetails}
                  />
                </div>

                <ProjectOrderImportExcel
                  refreshData={getDataSource}
                  projectId={projectId}
                  projectOrderId={projectDetails.id}
                />

                <Form.Check
                  checked={showDisabledRows}
                  onChange={(event) =>
                    setShowDisabledRows(event?.target.checked)
                  }
                  className='ms-4'
                  type='switch'
                  label={t('projectSettings.lbl_ShowDisabledRows')}
                />
              </div>
            </Col>

            {/* <Col className='d-flex justify-content-end'>
          <Button
            onClick={handleOpenRecentInquiries}
            className='btn btn-eleczap mx-3'
          >
            See recent Inquiries
          </Button>
          <Button onClick={handleOpenRecentOrders} className='btn btn-eleczap'>
            See recent Orders
          </Button>
        </Col> */}
          </Row>
        </Col>
        <Col />
        <Col className='text-end' lg={5}>
          <Row className='mb-2'>
            <Col>{t('projectSettings.lbl_RequestedDeliveryDate')}:</Col>
            <Col lg={5}>
              <DayPickerInput
                format='DD-MM-YYYY'
                dayPickerProps={{
                  fromMonth: new Date(),
                  disabledDays: disabledDays,
                }}
                style={{ width: '100%' }}
                component={(props: any) => (
                  <InputGroup>
                    <FormControl className='shorter-height-fields' {...props} />
                    <InputGroup.Text
                      onClick={handleDateDelete}
                      className='shorter-height-fields'
                      style={{ cursor: 'pointer' }}
                    >
                      <X className='icon-ez-red' size={21} />
                    </InputGroup.Text>
                  </InputGroup>
                )}
                value={requestedDate || t('common.lbl_DDMMYYYY')}
                onDayChange={handleDateChange}
              />
            </Col>
          </Row>
          <Row className='mb-0'>
            <Col>{t('addressSettings.lbl_DeliveryAddress')}:</Col>
            <Col lg={5}>
              <Form.Select
                className='shorter-height-fields'
                value={deliveryAddressId !== 0 ? deliveryAddressId : -1}
                style={{ width: '100%' }}
                onChange={handleDeliveryChange}
              >
                <option value={-1}>
                  {t('addressSettings.lbl_SelectDeliveryAddress')}
                </option>
                {filteredAddresses !== undefined &&
                  filteredAddresses.map((address, key) => (
                    <option value={address.id}>
                      {address.name} ({address.fullAddress})
                    </option>
                  ))}
                <DeliveryAddressesNew
                  asOption
                  asOptionShow={asOptionShow}
                  setAsOptionShow={setAsOptionShow}
                  setAddresses={setAddresses}
                  setNewDeliverAddressAsSelected={
                    setNewDeliverAddressAsSelected
                  }
                  t={t}
                />
              </Form.Select>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* <RecentDocsDrawer
        shownView={shownView}
        show={show}
        handleClose={handleClose}
      /> */}
    </Card.Header>
  );
};
